import { createContext, MutableRefObject, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { GridFilterModel, GridRowId, useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { ISalesLog } from "@DataPages/types/salesOperations/salesLog/model";

// Constants
import { TAB_NAMES } from "@/DataPages/constants/sales-operations";

// Reducer
import { getInitialState, getInitialStateByTab, handleSetInclude, includedCheckboxChanged } from "./includeReducer";

// Types
import { TIncludedAction, TIncludedState } from "@/DataPages/types/salesOperations/salesLog/utils";

type SalesLogChartContextValue = {
	includedDealType: TIncludedState;
	setIncludedDealType: React.Dispatch<TIncludedAction>;
	apiRef: MutableRefObject<GridApiPremium>;
	onDataChange: (data: ISalesLog[]) => void;
	onFilterModelChange: (model: GridFilterModel) => void;
	tabSelected: string;
	setTabSelected: (tab: string) => void;
	filteredRows: GridRowId[];
	setIsFiltering: (value: boolean) => void;
	setFilteredRows: (rows: GridRowId[]) => void;
	shouldReCalculateAnalytics: boolean;
	setOutdatedAnalytics: (value: boolean) => void;
};

const SalesLogChartsContext = createContext<SalesLogChartContextValue>({
	includedDealType: {} as TIncludedState,
	setIncludedDealType: () => {},
	apiRef: { current: {} as GridApiPremium },
	onDataChange() {},
	onFilterModelChange() {},
	tabSelected: "",
	setTabSelected() {},
	filteredRows: [],
	setIsFiltering() {},
	setFilteredRows() {},
	shouldReCalculateAnalytics: false,
	setOutdatedAnalytics() {},
});

type SalesLogChartProviderProps = {
	children: ReactNode;
};

function SalesLogChartProvider({ children }: Readonly<SalesLogChartProviderProps>) {
	const apiRef = useGridApiRef();

	const [tabSelected, setTabSelected] = useState("");
	const [filteredRows, setFilteredRows] = useState<GridRowId[]>([]);
	const [isFiltering, setIsFiltering] = useState(false);
	const [includedDealType, setIncludedDealType] = useReducer(handleSetInclude, getInitialState());
	const [outdatedAnalytics, setOutdatedAnalytics] = useState(false);

	const updateFilteredRows = useCallback((rows: Record<GridRowId, boolean>) => {
		const newRows = Object.keys(rows).filter((key) => rows[key]);
		setFilteredRows(newRows);
	}, []);

	const onFilterModelChange = useCallback(
		(model: GridFilterModel) => {
			if (!apiRef.current) {
				return;
			}
			setIsFiltering(Boolean(model.items.length > 0 || model.quickFilterValues?.length));
			const { filteredRowsLookup } = apiRef.current.getFilterState(model);
			updateFilteredRows(filteredRowsLookup);
		},
		[apiRef]
	);

	const onDataChange = useCallback(
		(data: ISalesLog[]) => {
			if (!apiRef.current?.state?.filter?.filteredRowsLookup) {
				if (!data?.length) {
					return;
				}
				const filteredData = data?.filter((item) => Boolean(item._id));
				const dataLookup = filteredData?.reduce(
					(acc, cur) => {
						if (!cur?._id) {
							return acc;
						}
						acc[cur?._id] = true;
						return acc;
					},
					{} as Record<string, boolean>
				);
				updateFilteredRows(dataLookup);
				return;
			}

			updateFilteredRows(apiRef.current.state.filter.filteredRowsLookup);
		},
		[apiRef.current?.state?.filter?.filteredRowsLookup, updateFilteredRows]
	);

	const shouldReCalculateAnalytics = useMemo(() => {
		if (outdatedAnalytics) {
			return true;
		}

		return isFiltering || includedCheckboxChanged(includedDealType, tabSelected);
	}, [includedDealType, tabSelected, isFiltering, outdatedAnalytics]);

	useEffect(() => {
		if (tabSelected) {
			const stateByTab = getInitialStateByTab(tabSelected as (typeof TAB_NAMES)[keyof typeof TAB_NAMES]);
			setIncludedDealType({ type: "*", payload: stateByTab });
		}
	}, [tabSelected]);

	const contextValue = useMemo(
		() => ({
			includedDealType,
			setIncludedDealType,
			apiRef,
			onDataChange,
			onFilterModelChange,
			tabSelected,
			setTabSelected,
			filteredRows,
			setIsFiltering,
			setFilteredRows,
			shouldReCalculateAnalytics,
			setOutdatedAnalytics,
		}),
		[
			includedDealType,
			setIncludedDealType,
			apiRef,
			onFilterModelChange,
			tabSelected,
			setTabSelected,
			filteredRows,
			setIsFiltering,
			setFilteredRows,
			shouldReCalculateAnalytics,
			setOutdatedAnalytics,
		]
	);

	return <SalesLogChartsContext.Provider value={contextValue}>{children}</SalesLogChartsContext.Provider>;
}

function useSalesLogChart() {
	const context = useContext(SalesLogChartsContext);
	if (context === undefined) {
		throw new Error("useSalesLogChart must be used within a SalesLogChartProvider");
	}
	return context;
}

export { SalesLogChartProvider, useSalesLogChart };
